/* eslint-disable react/no-danger */
import React from 'react'
import { convertToSlug } from 'src/utils/IdGenerator'
import ErrorBoundary from '@components/ErrorBoundary/errorBoundary'
import { baseUrl } from '@components/Product/ProductHead/utils'
import { parsePromiseResponse } from '@utils/promiseResponse'
import assetLoader from '@utils/internalImageLoader'
import ClickableImage from '@components/ClickableImage/ClickableImage'
import { getContentFromCMS } from '@quark/cms'
import { ContentType } from '@enums/ContentType'
import { LatticeEntryState, SortBy } from '@enums/strapiTypesAndOperators'
import { getFilters } from '@helpers/interlinkingFilters'
import { DYNAMIC_URLS_PAGE_LIMIT } from '@constants/strapi'
import { UrlAttributes } from './interface'
import styles from './DynamicInterlinking.module.scss'

const getProductPageUrls = async pageFooterTagName => {
  const env = process.env.ACKO_ENV
  const res = await getContentFromCMS({
    env,
    token: process.env.LATTICE_CMS_API_TOKEN,
    contentType: ContentType.Productpage,
    filters: getFilters(pageFooterTagName),
    sort: SortBy.CREATED_AT,
    limit: DYNAMIC_URLS_PAGE_LIMIT,
    headerType: 'v4',
    status: env === 'prod' ? LatticeEntryState.Published : LatticeEntryState.Draft,
  })
  return res
}

const getProductUrls = async pageFooterTagName => {
  const env = process.env.ACKO_ENV
  const res = await getContentFromCMS({
    env,
    token: process.env.LATTICE_CMS_API_TOKEN,
    contentType: ContentType.Product,
    filters: getFilters(pageFooterTagName),
    sort: SortBy.CREATED_AT,
    limit: DYNAMIC_URLS_PAGE_LIMIT,
    headerType: 'v4',
    status: env === 'prod' ? LatticeEntryState.Published : LatticeEntryState.Draft,
  })
  return res
}

const getblogsUrls = async pageFooterTagName => {
  const env = process.env.ACKO_ENV
  const res = await getContentFromCMS({
    env,
    token: process.env.LATTICE_CMS_API_TOKEN,
    contentType: ContentType.Blogs,
    filters: getFilters(pageFooterTagName),
    sort: SortBy.CREATED_AT,
    limit: DYNAMIC_URLS_PAGE_LIMIT,
    headerType: 'v4',
    status: env === 'prod' ? LatticeEntryState.Published : LatticeEntryState.Draft,
  })
  return res
}

const getDynamicUrls = async footerHeadings => {
  const dynamicUrls: any = []

  for (let i = 0; i < footerHeadings?.length; i++) {
    const tagName = footerHeadings[i]?.attributes?.pageFooterTagName
    // eslint-disable-next-line no-continue
    if (!tagName || tagName === 'undefined') continue
    const responses = await Promise.allSettled([
      getProductPageUrls(tagName),
      getProductUrls(tagName),
      getblogsUrls(tagName),
    ])
    const productPageUrls = parsePromiseResponse(responses?.[0])
    const productUrls = parsePromiseResponse(responses?.[1])
    const blogPageUrls = parsePromiseResponse(responses?.[2])

    let urls: any = []

    if (Array.isArray(productUrls) && productUrls?.length) {
      urls = [...productUrls]
    }
    if (Array.isArray(productPageUrls) && productPageUrls?.length) {
      urls = [...urls, ...productPageUrls]
    }
    if (Array.isArray(blogPageUrls) && blogPageUrls?.length) {
      urls = [...urls, ...blogPageUrls]
    }

    const uniqueUrls = Array.from(new Map(urls.map(url => [url?.attributes?.url, url])).values())

    const formatUrls = uniqueUrls?.map((url: UrlAttributes) => ({
      url: url?.attributes?.url,
      name: url?.attributes?.anchorTextForPageFooter,
    }))
    dynamicUrls.push({
      heading: footerHeadings[i]?.attributes?.pageFooterTableName,
      urls: formatUrls,
    })
  }
  return dynamicUrls
}

const DynamicInterlinking = async ({ data }) => {
  const heading = data?.data?.attributes?.DynamicInterlinkingHeading
  const footerHeadings = data?.data?.attributes?.Content?.data
  const dynamicUrls = await getDynamicUrls(footerHeadings)

  if (!dynamicUrls?.length) {
    return null
  }

  // Extract the h2 content and headerTabName
  const h2Regex = /<h2[^>]*>(.*?)<\/h2>/
  const matches = heading?.match(h2Regex)
  const h2Content = matches ? matches[1] : null
  const idContent = data?.data?.attributes?.headerTabName || h2Content

  return (
    <ErrorBoundary data={data}>
      {dynamicUrls[0]?.heading && dynamicUrls[0]?.urls && (
        <div className={`${styles.dynamicInterlinking} white`} id={convertToSlug(idContent)}>
          <div dangerouslySetInnerHTML={{ __html: heading }} className={styles.dynamicInterlinkingHeading} />
          {dynamicUrls?.map((item, index) => {
            if (item?.urls?.length === 0) return null
            return (
              <div className={styles.sectionStyle}>
                <div key={index}>
                  <h3>{item?.heading}</h3>
                  <div className={styles.linkSection}>
                    {item?.urls?.map((url, ind) => (
                      <div className={styles.urlTag}>
                        <a href={`${baseUrl}${url.url}`} target="_blank" key={ind}>
                          {url.name}
                        </a>
                        <ClickableImage
                          src={`${assetLoader()}/images/linktag.png`}
                          width={20}
                          height={20}
                          alt="Click Link"
                          imageStyles={styles.imge}
                          loading="lazy"
                          redirectionUrl={`${baseUrl}${url.url}`}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </ErrorBoundary>
  )
}

export default DynamicInterlinking
